import React from 'react';
import cn from 'classnames';
import _ from 'lodash';

import { confirm, pasteViewSettingsModal } from '../../../../../../common/Modal';
import Icon from '../../../../../../common/UI/Icon';
import getLink from '../../../../../../common/router/getLink';

import { checkAccessOnObject } from '../../../../../../../utils/rights';
import PRIVILEGE_CODES from '../../../../../../../configs/privilegeCodes';
import RESOURCE_TYPES from '../../../../../../../configs/resourceTypes';
import { SCOPES } from '../../../../../../../configs/viewSettings';

import apiActions from '../../../../../../../actions/apiActions';
import modalsActions from '../../../../../../../actions/modalsActions';
import viewActions from '../../../../../../../actions/viewActions';
import viewSettingsActions from '../../../../../../../actions/viewSettingsActions';

import routes, { VIEW_MODE_ROUTES } from '../../../../../../../routes';

import { prepareStateToApi, getViewModeFromPath } from '../../../../../../../utils/viewSettings';
import { resolveViewType, VIEW_TYPES } from '../../../../../../../utils/viewType';

import styles from './actions.less';

function viewHasGlobalSettings(view, viewId) {
    const viewType = resolveViewType(view);
    return viewType === VIEW_TYPES.SHARED || viewType === VIEW_TYPES.GLOBAL || String(viewId) === '0';
}

export function viewItems(inGroup) {
  const {
    t,
    viewId,
    catalogId,
    catalog,
    view,
    viewSettingsChanged,
    hasPersonalOverrides,
    views,
    viewSettingsClipboard,
  } = this.props;

  // view state
  const isCatalogAdmin = checkAccessOnObject(RESOURCE_TYPES.CATALOG, catalog, PRIVILEGE_CODES.ADMIN);
  const isViewAdmin = this.isViewAdmin();
  const viewType = resolveViewType(view);

  const isPersonalView = viewType === VIEW_TYPES.PERSONAL;
  const canPersistCurrentView = isPersonalView || isCatalogAdmin;
  const isNewView = viewId === '$new';
  const isDefaultView = String(viewId) === '0';
  const filtersChanged = view && view.get('filtersChanged');

  const hasChanges = filtersChanged || viewSettingsChanged;
  const primarySaveText = isDefaultView ? t('buttons.saveLayout') : t('buttons.saveView');

  // условия видимости пунктов меню
  const canManage = !isNewView && !isDefaultView && !!view && isViewAdmin;

  const saveItems = [];
  const manageItems = [];

  // save view
  const canSaveAsDefault = isDefaultView && isCatalogAdmin && (hasChanges ? viewSettingsChanged : hasPersonalOverrides);
  const canSaveView =
    !isDefaultView && !isNewView && isViewAdmin && canPersistCurrentView && (hasChanges || hasPersonalOverrides);
  const canSaveAsNew = (isNewView || hasChanges || hasPersonalOverrides) && canPersistCurrentView;
  if (canSaveAsDefault)
    saveItems.push({
      key: 'viewSaveSettings',
      icon: 'transfers-48',
      text: t('buttons.saveLayout'),
      badge: true,
      onClick: this.viewSaveSettings,
    });
  if (canSaveView)
    saveItems.push({
      key: 'viewSave',
      icon: 'transfers-48',
      text: primarySaveText,
      badge: true,
      onClick: hasChanges ? this.viewSave : this.viewSaveSettings,
    });
  if (canSaveAsNew)
    saveItems.push({
      key: 'viewSaveNew',
      icon: 'transfers-46',
      text: t('buttons.saveAsNewView'),
      badge: true,
      onClick: this.viewSaveNew,
    });

  // reset display settings
  if (hasChanges || (!isNewView && hasPersonalOverrides))
    saveItems.push({
      key: 'viewResetSettings',
      icon: 'transfers-70',
      text: t('buttons.resetViewChanges'),
      onClick: this.viewResetSettings,
    });

  // edit view
  if (canManage) {
    manageItems.push({
      key: 'viewEdit',
      icon: 'edition-33',
      text: t('views.renameView'),
      onClick: this.viewEdit,
    });
  }

  // access
  if (canManage && viewType === VIEW_TYPES.SHARED) {
    manageItems.push({
      key: 'viewAccess',
      icon: 'edition-55',
      iconClassName: styles.settingIconAccess,
      text: t('buttons.accessView'),
      onClick: this.viewAccess,
    });
  }

  // copy settings
  const canCopySettings = views?.size > 1 && !isNewView && !!view;
  if (canCopySettings)
    manageItems.push({
      key: 'viewCopySettings',
      icon: 'multimedia-12',
      text: t('buttons.copyViewSettings'),
      onClick: this.viewCopySettings,
    });

  // paste settiтgs
  const clipCatalogId = viewSettingsClipboard && viewSettingsClipboard.get('catalogId');
  const clipViewId = viewSettingsClipboard && viewSettingsClipboard.get('viewId');
  const canPasteSettings =
    !!viewSettingsClipboard &&
    String(clipCatalogId) === String(catalogId) &&
    String(clipViewId) !== String(viewId) &&
    !isNewView &&
    !!view;
  if (canPasteSettings)
    manageItems.push({
      key: 'viewPasteSettings',
      icon: 'status-26',
      text: t('buttons.pasteViewSettings'),
      onClick: this.viewPasteSettings,
    });

  // remove view
  if (canManage) {
    manageItems.push({
      key: 'viewRemove',
      icon: 'edition-41',
      text: t('buttons.removeView'),
      className: styles.settingRemove,
      onClick: this.viewRemove,
    });
  }

  const toMenuItems = (items) =>
    items.map((item) => ({
      key: item.key,
      label: (
        <a onClick={item.onClick} className={item.className ? item.className : null}>
          {item.badge ? (
            <span className={styles.settingBadge}>
              <Icon type={`icon ${item.icon}`} className={cn(styles.settingIcon, item.iconClassName)} />
            </span>
          ) : (
            <Icon type={`icon ${item.icon}`} className={cn(styles.settingIcon, item.iconClassName)} />
          )}
          <span>{item.text}</span>
        </a>
      ),
    }));

  let menu = [
    ...toMenuItems(saveItems),
    saveItems.length > 0 &&
      manageItems.length > 0 && {
        key: 'divider',
        type: 'divider',
      },
    ...toMenuItems(manageItems),
  ].filter(Boolean);

  if (inGroup && menu.length) {
    menu = [
      {
        key: '2',
        type: 'group',
        label: view ? `${t('common.view')} «${view.get('displayName')}»` : t('common.view'),
        children: menu,
      },
    ];
  }

  return menu;
}
export function viewBadge() {
  const { view, viewSettingsChanged, hasPersonalOverrides } = this.props;

  // view state
  const filtersChanged = view && view.get('filtersChanged');
  const hasChanges = filtersChanged || viewSettingsChanged;

  return hasChanges || hasPersonalOverrides;
}

// edit view
export function viewEdit(e) {
  const { view, sceneId } = this.props;
  const catalogId = view.get('catalogId');
  const oldViewId = view.get('id');

  modalsActions.openViewInputModalEdit(view, catalogId, sceneId, (params, result) => {
    const { location, history } = this.props;
    const newViewId = (result && result.id) || oldViewId;
    const idChanged = String(newViewId) !== String(oldViewId);
    const lsKey = `catalog.${catalogId}.defaultViewId`;
    const targetLink = getLink(location, VIEW_MODE_ROUTES[params.viewMode] || null, { viewId: newViewId });

    if (idChanged) {
      if (localStorage.getItem(lsKey) === String(oldViewId)) {
        localStorage.setItem(lsKey, String(newViewId));
      }
      history.push(targetLink);
      viewActions.truncateView({ viewId: oldViewId, catalogId, sceneId });
    } else if (params.viewMode) {
      history.push(getLink(location, VIEW_MODE_ROUTES[params.viewMode] || null, {}));
    }
  });
}

// save views
export async function viewSave() {
  const filtersChanged = this.props.view && this.props.view.get('filtersChanged');
  const { viewSettingsChanged } = this.props;

  if (filtersChanged) {
    this.viewSaveFilters();
  }
  if (viewSettingsChanged) {
    await this.viewSaveSettings();
  }
}

export async function viewSaveSettings(e) {
  const { viewId, catalogId, view } = this.props;

  const viewModeSettings =
    this.props.viewSettings && this.props.viewSettings.getIn([String(catalogId), 'views', String(viewId)]);
  if (viewModeSettings) {
    const settingsToSave = viewModeSettings.delete('hasChanges').toJS();
    const prepareSettings = prepareStateToApi(settingsToSave);

    const isDefaultView = String(viewId) === '0';
    const viewType = isDefaultView ? VIEW_TYPES.GLOBAL : resolveViewType(view);
    const targetScope = this.viewValidateType(viewType);

    await viewSettingsActions.batchUpdateSettings({
      catalogId,
      viewId,
      scope: targetScope,
      settings: prepareSettings,
    });
    viewSettingsActions.loadViewSettings({ catalogId, viewId });
  }
}

export function viewSaveFilters(e) {
  const { sceneId, view } = this.props;

  const params = {
    name: (view && view.get('name')) || (view && view.get('originName')),
    type: resolveViewType(view),
    forRights: view && view.get('forRights'),
  };

  // Get new filters from scene view, current filters + new ones
  const filters = view && view.get('filters');
  viewActions.updateExistingView(sceneId, view, params, filters, () => {
    const { location, history } = this.props;
    history.push(location.pathname);
  });
}

export function viewSaveNew() {
  const { sceneId, viewId, catalog, catalogId, location } = this.props;

  // catalog right
  const accessOnViewForRights = checkAccessOnObject(RESOURCE_TYPES.CATALOG, catalog, PRIVILEGE_CODES.ACCESS);
  const viewMode = getViewModeFromPath(location.pathname);

  modalsActions.openViewInputModal(
    sceneId,
    viewId,
    accessOnViewForRights,
    async (res) => {
      const { history } = this.props;
      const nextType = (res && res.type) || VIEW_TYPES.PERSONAL;
      const targetScope = this.viewValidateType(nextType);

      // Copy settings to default scope only for admin-created global views.
      const viewModeSettings =
        this.props.viewSettings && this.props.viewSettings.getIn([String(catalogId), 'views', String(viewId)]);
      if (viewModeSettings) {
        const settingsToSave = viewModeSettings.delete('hasChanges').delete('hasPersonalSettings').toJS();
        const preparedSettings = prepareStateToApi(settingsToSave);
        if (Object.keys(preparedSettings).length > 0) {
          await viewSettingsActions.batchUpdateSettings({
            catalogId,
            viewId: res.id,
            scope: targetScope,
            settings: preparedSettings,
          });
        }
      }

      location.search = '';
      history.push(getLink(location, VIEW_MODE_ROUTES[res.viewMode] || null, { viewId: res.id }));
    },
    viewMode,
  );
}

// access
export function viewAccess() {
  const { view, location, history } = this.props;

  if (view) {
    const isAccessView = view && checkAccessOnObject(RESOURCE_TYPES.VIEW, view, PRIVILEGE_CODES.ACCESS);
    const readOnly =
      !checkAccessOnObject(RESOURCE_TYPES.CATALOG, this.props.catalog, PRIVILEGE_CODES.ACCESS) && !isAccessView;
    modalsActions.openViewAccessModal(view, readOnly, (result) => {
      if (result && result.viewId) {
        const link = getLink(location, routes.view, {
          viewId: result.viewId,
        });
        history.push(link);
      }
    });
  }
}

// remove view
export function viewRemove(e) {
  const { t, sceneId, viewId, view, history, location } = this.props;
  const catalogId = view.get('catalogId');

  confirm({
    headerText: t('modals.removeViewConfirm.headerText'),
    text:
      resolveViewType(view) === VIEW_TYPES.SHARED
        ? t('modals.removeViewConfirm.textForRights')
        : t('modals.removeViewConfirm.text'),
    okText: t('modals.removeViewConfirm.okText'),
    cancelText: t('modals.removeViewConfirm.cancelText'),
    onOk() {
      apiActions.deleteView({
        catalogId,
        viewId,
        sceneId,
      });
      history.push(getLink(location, null, { viewId: 0 }));
    },
  });
}

// view display settings
export async function viewResetSettings(e) {
  const { sceneId, viewId, catalogId, location, history } = this.props;

  viewActions.resetView(sceneId, viewId);

  await viewSettingsActions.batchUpdateSettings({ catalogId, viewId, scope: SCOPES.PERSONAL, settings: {} });
  viewSettingsActions.loadViewSettings({ catalogId, viewId, merged: false });

  if (viewId === '$new') {
    history.push(getLink(location, null, { viewId: 0 }));
  }
}

export function viewCopySettings() {
  const { catalogId, viewId, viewSettings } = this.props;
  const viewModeSettings = viewSettings && viewSettings.getIn([String(catalogId), 'views', String(viewId)]);
  if (!viewModeSettings) return;
  const cleaned = viewModeSettings.delete('hasChanges').delete('hasPersonalSettings');
  viewSettingsActions.setViewSettingsClipboard({
    catalogId,
    viewId,
    settings: cleaned.toJS(),
  });
}

export function viewPasteSettings() {
  const { catalogId, viewId, viewSettingsClipboard, location } = this.props;
  if (!viewSettingsClipboard) return;

  const clipSettings = viewSettingsClipboard.get('settings').toJS();
  const currentMode = getViewModeFromPath(location.pathname);

  pasteViewSettingsModal({
    currentMode,
    onOk: (selectedModes) => {
      if (!selectedModes || selectedModes.length === 0) return;

      const filteredSettings = {};
      for (const mode of selectedModes) {
        if (clipSettings[mode]) {
          filteredSettings[mode] = clipSettings[mode];
        }
      }

      viewSettingsActions.mergeViewSettingsToAppState({
        catalogId,
        viewId,
        settings: { ...filteredSettings, hasChanges: true },
      });

      const preparedSettings = prepareStateToApi(filteredSettings);
      viewSettingsActions.patchBatchSettings({
        catalogId,
        viewId,
        scope: SCOPES.PERSONAL,
        settings: preparedSettings,
      });
    },
  });
}

// helpers
export function isViewAdmin() {
  const { catalog, view } = this.props;
  const catalogPrivilegeCode = catalog && catalog.get('privilegeCode');
  const viewType = resolveViewType(view);

  // is my private view
  const isPrivateView = viewType === VIEW_TYPES.PERSONAL;
  if (isPrivateView) {
    return true;
  }

  if (viewType === VIEW_TYPES.GLOBAL) {
    return checkAccessOnObject(RESOURCE_TYPES.CATALOG, catalog, PRIVILEGE_CODES.ADMIN);
  }

  // is admin of catalog
  if (catalogPrivilegeCode === PRIVILEGE_CODES.ADMIN) {
    return true;
  }

  // has right to view
  const isViewAdmin = checkAccessOnObject(RESOURCE_TYPES.VIEW, view, PRIVILEGE_CODES.ACCESS);
  if (isViewAdmin) {
    return true;
  }
}

export function viewValidateType(viewType) {
  const { catalog } = this.props;
  const canEditGlobal = checkAccessOnObject(RESOURCE_TYPES.CATALOG, catalog, PRIVILEGE_CODES.ADMIN);

  if (viewType === VIEW_TYPES.PERSONAL) {
    return SCOPES.PERSONAL;
  }

  if (viewType === VIEW_TYPES.SHARED || viewType === VIEW_TYPES.GLOBAL) {
    return canEditGlobal ? SCOPES.GLOBAL : SCOPES.PERSONAL;
  }

  return SCOPES.PERSONAL;
}
